/**
 * Cart TanStack Query Hooks
 *
 * Complete cart state management with:
 * - Optimistic updates for instant UI feedback
 * - Automatic cache invalidation
 * - Error rollback
 * - Graceful error handling for cart operations
 *
 * @file useCart.ts
 * @version 2.1.0 - Added resilient error handling
 */

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddToCartRequest,
  CartLineItem,
  CartResponse,
  CartTotalResponse,
} from '../api/cart.api';
import {
  addToCart,
  clearCart,
  getCart,
  getCartTotal,
  removeCartItem,
  updateCartCountry,
  updateCartItem,
} from '../api/cart.api';
import { FUNNEL_EVENTS } from '@/shared/analytics';
import { trackFunnelEvent } from '@/shared/hooks/useAnalytics';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const cartKeys = {
  all: ['cart'] as const,
  items: () => [...cartKeys.all, 'items'] as const,
  /** Base key for all cart total queries - use for invalidation */
  totalBase: () => [...cartKeys.all, 'total'] as const,
  /** Full key for specific shipping method - use for queries */
  total: (shippingMethodId?: string) =>
    [...cartKeys.totalBase(), shippingMethodId ?? 'default'] as const,
};

// ============================================================================
// ERROR HANDLING UTILITIES
// ============================================================================

/**
 * Determines if an error should trigger a retry for cart operations.
 *
 * Cart operations are session-based and should NOT require authentication.
 * If we get a 401, it's likely a misclassified error from the backend
 * (e.g., a database error with "token" in the message being misclassified).
 *
 * We retry most errors because cart operations should be resilient.
 * We DON'T retry on clear validation errors (4xx except 401).
 */
function shouldRetryCartError(failureCount: number, error: Error): boolean {
  // Max 3 retries
  if (failureCount >= 3) return false;

  const errorMessage = error.message.toLowerCase();

  // Don't retry on clear validation errors (400, 422)
  if (
    errorMessage.includes('400') ||
    errorMessage.includes('422') ||
    errorMessage.includes('validation')
  ) {
    return false;
  }

  // Don't retry on rate limiting (429)
  if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
    return false;
  }

  // Retry on 401 - for cart operations, this is likely a misclassified error
  // (cart operations don't require authentication)
  if (errorMessage.includes('401')) {
    return true;
  }

  // Retry on server errors (5xx)
  if (
    errorMessage.includes('500') ||
    errorMessage.includes('502') ||
    errorMessage.includes('503') ||
    errorMessage.includes('504')
  ) {
    return true;
  }

  // Default: retry on unknown errors
  return true;
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch cart data
 *
 * Uses resilient error handling:
 * - Retries on transient errors (including misclassified 401s)
 * - Exponential backoff between retries
 */
export function useCart(): UseQueryResult<CartResponse> {
  return useQuery({
    queryKey: cartKeys.items(),
    queryFn: () => getCart(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: shouldRetryCartError,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff, max 10s
  });
}

/**
 * Get cart item count (total quantity across all items)
 */
export function useCartItemCount(): number {
  const { data } = useCart();

  if (!data?.items) return 0;

  return data.items.reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * Fetch cart totals with VAT breakdown
 *
 * Uses resilient error handling:
 * - Retries on transient errors (including misclassified 401s)
 * - Exponential backoff between retries
 *
 * @param shippingMethodId - Optional shipping method UUID to calculate accurate shipping cost
 */
export function useCartTotal(shippingMethodId?: string): UseQueryResult<CartTotalResponse> {
  return useQuery({
    queryKey: cartKeys.total(shippingMethodId),
    queryFn: () => getCartTotal({ shippingMethodId }),
    staleTime: 1 * 60 * 1000, // 1 minute (totals change with quantity)
    retry: shouldRetryCartError,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff, max 10s
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Add item to cart with optimistic update
 */
export function useAddToCart(): UseMutationResult<
  CartResponse,
  Error,
  AddToCartRequest,
  { previousCart: CartResponse | undefined }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AddToCartRequest) => addToCart(request),

    // Optimistic update
    onMutate: async (newItem) => {
      // Cancel any outgoing refetch operations
      await queryClient.cancelQueries({ queryKey: cartKeys.items() });

      // Snapshot current value
      const previousCart = queryClient.getQueryData<CartResponse>(cartKeys.items());

      // Optimistically update with a placeholder item
      if (previousCart) {
        const optimisticItem: CartLineItem = {
          id: `optimistic-${Date.now()}`,
          // Use product_variant_id if provided, otherwise use product_type_id as placeholder
          product_variant_id: newItem.product_variant_id ?? newItem.product_type_id ?? 'pending',
          quantity: newItem.quantity ?? 1,
          price_minor_units: 0,
          currency: 'USD',
          vat_amount_minor_units: 0,
          vat_percentage: 0.25,
          product_snapshot: {
            product_type_name: 'Adding...',
          },
        };

        queryClient.setQueryData<CartResponse>(cartKeys.items(), {
          ...previousCart,
          items: [...previousCart.items, optimisticItem],
        });
      }

      return { previousCart };
    },

    // On success, replace optimistic update with real data
    onSuccess: (data, variables) => {
      queryClient.setQueryData(cartKeys.items(), data);
      // Invalidate ALL cart total queries (regardless of shipping method)
      void queryClient.invalidateQueries({ queryKey: cartKeys.totalBase() });

      // Track cart item added
      // Find the added item by product_variant_id (the only ID available in CartLineItem)
      // If no match found, use the last item as it's likely the newly added one
      const addedItem = variables.product_variant_id
        ? data.items.find((item) => item.product_variant_id === variables.product_variant_id)
        : data.items[data.items.length - 1];

      if (addedItem) {
        const productSnapshot = addedItem.product_snapshot as
          | { product_type_name?: string; design_name?: string }
          | undefined;
        const designName = productSnapshot?.design_name;
        trackFunnelEvent(FUNNEL_EVENTS.CART_ITEM_ADDED, {
          product_id: addedItem.product_variant_id,
          price: addedItem.price_minor_units,
          currency: addedItem.currency || 'USD',
          product_type: productSnapshot?.product_type_name ?? 'unknown',
          source: 'product_configurator',
          // Conditionally spread design_id only if it exists (exactOptionalPropertyTypes)
          ...(designName ? { design_id: designName } : {}),
        });
      }
    },

    // On error, rollback
    onError: (_err, _newItem, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(cartKeys.items(), context.previousCart);
      }
    },
  });
}

/**
 * Update cart item quantity with optimistic update
 */
export function useUpdateCartItem(): UseMutationResult<
  CartResponse,
  Error,
  { itemId: string; quantity: number },
  { previousCart: CartResponse | undefined }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: string; quantity: number }) =>
      updateCartItem(itemId, quantity),

    onMutate: async ({ itemId, quantity }) => {
      await queryClient.cancelQueries({ queryKey: cartKeys.items() });

      const previousCart = queryClient.getQueryData<CartResponse>(cartKeys.items());

      if (previousCart) {
        queryClient.setQueryData<CartResponse>(cartKeys.items(), {
          ...previousCart,
          items: previousCart.items.map((item) =>
            item.id === itemId ? { ...item, quantity } : item
          ),
        });
      }

      return { previousCart };
    },

    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(cartKeys.items(), data);
      // Invalidate ALL cart total queries (regardless of shipping method)
      void queryClient.invalidateQueries({ queryKey: cartKeys.totalBase() });

      // Track cart item updated
      const updatedItem = data.items.find((item) => item.id === variables.itemId);
      const previousItem = context.previousCart?.items.find((item) => item.id === variables.itemId);

      if (updatedItem && previousItem) {
        trackFunnelEvent(FUNNEL_EVENTS.CART_ITEM_UPDATED, {
          product_id: updatedItem.product_variant_id,
          old_quantity: previousItem.quantity,
          new_quantity: variables.quantity,
          price: updatedItem.price_minor_units,
          currency: updatedItem.currency || 'USD',
        });
      }
    },

    onError: (_err, _vars, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(cartKeys.items(), context.previousCart);
      }
    },
  });
}

/**
 * Remove item from cart with optimistic update
 */
export function useRemoveCartItem(): UseMutationResult<
  CartResponse,
  Error,
  string,
  { previousCart: CartResponse | undefined }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => removeCartItem(itemId),

    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: cartKeys.items() });

      const previousCart = queryClient.getQueryData<CartResponse>(cartKeys.items());

      if (previousCart) {
        queryClient.setQueryData<CartResponse>(cartKeys.items(), {
          ...previousCart,
          items: previousCart.items.filter((item) => item.id !== itemId),
        });
      }

      return { previousCart };
    },

    onSuccess: (data, itemId, context) => {
      queryClient.setQueryData(cartKeys.items(), data);
      // Invalidate ALL cart total queries (regardless of shipping method)
      void queryClient.invalidateQueries({ queryKey: cartKeys.totalBase() });

      // Track cart item removed
      const removedItem = context.previousCart?.items.find((item) => item.id === itemId);

      if (removedItem) {
        trackFunnelEvent(FUNNEL_EVENTS.CART_ITEM_REMOVED, {
          product_id: removedItem.product_variant_id,
          reason: 'user_removed',
          price: removedItem.price_minor_units,
        });
      }
    },

    onError: (_err, _itemId, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(cartKeys.items(), context.previousCart);
      }
    },
  });
}

/**
 * Clear all items from cart
 */
export function useClearCart(): UseMutationResult<
  CartResponse,
  Error,
  void,
  { previousCart: CartResponse | undefined }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => clearCart(),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: cartKeys.items() });

      const previousCart = queryClient.getQueryData<CartResponse>(cartKeys.items());

      if (previousCart) {
        queryClient.setQueryData<CartResponse>(cartKeys.items(), {
          ...previousCart,
          items: [],
          total_amount_major: 0,
        });
      }

      return { previousCart };
    },

    onSuccess: (data) => {
      queryClient.setQueryData(cartKeys.items(), data);
      // Invalidate ALL cart total queries (regardless of shipping method)
      void queryClient.invalidateQueries({ queryKey: cartKeys.totalBase() });
    },

    onError: (_err, _vars, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(cartKeys.items(), context.previousCart);
      }
    },
  });
}

/**
 * Update cart country and recalculate all prices
 *
 * When user selects a different country in checkout, this mutation:
 * - Recalculates all product prices for the new country
 * - Updates VAT rates based on country tax rules
 * - Converts all prices to the country's currency
 * - Updates add-on prices
 *
 * Invalidates all cart queries on success to refresh UI with new currency/prices.
 */
export function useUpdateCartCountry(): UseMutationResult<CartResponse, Error, string> {
  const queryClient = useQueryClient();

  return useMutation<CartResponse, Error, string>({
    mutationFn: updateCartCountry,

    onSuccess: (data) => {
      // Update cart items cache with new data (includes updated prices/currency)
      queryClient.setQueryData(cartKeys.items(), data);
      // Invalidate all cart totals (they need fresh calculation with new country)
      void queryClient.invalidateQueries({ queryKey: cartKeys.totalBase() });

      // Track cart refresh after country change
      const totalValue = data.items.reduce(
        (sum, item) => sum + item.price_minor_units * item.quantity,
        0
      );
      trackFunnelEvent(FUNNEL_EVENTS.CART_VIEWED, {
        item_count: data.items.length,
        total_value: totalValue,
        currency: data.currency,
      });
    },

    onError: () => {
      // Error is logged by global mutation cache (see query-client.ts)
    },
  });
}

// Re-export types for convenience
;
